'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import {
  Star, MapPin, BadgeCheck, Zap, Crown, Phone, MessageSquare,
  Share2, Heart, ChevronRight, Award, Clock, Briefcase, Calendar } from
'lucide-react';
import AppImage from '@/components/ui/AppImage';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';


const PROVIDER = {
  id: 'prov-001',
  name: 'James Hartley Plumbing',
  trade: 'Plumber & Gas Engineer',
  tier: 'premium' as const,
  location: 'Clapham, SW4',
  postcode: 'SW4 7NB',
  distanceMiles: 1.2,
  rating: 4.9,
  reviewCount: 214,
  jobsDone: 380,
  responseTime: '< 1 hr',
  memberSince: 'Jan 2023',
  completionRate: 97,
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_139e4448e-1772451223398.png",
  avatarAlt: 'James Hartley, smiling male plumber in blue workwear',
  coverBg: 'from-plum-900 to-plum-700',
  verified: true,
  availableNow: true,
  tagline: 'Emergency callouts covered 24/7. Gas Safe registered. 10+ years in SW London.',
  endorsementCount: 18
};

const TierBadge = ({ tier }: {tier: 'free' | 'pro' | 'premium';}) => {
  if (tier === 'premium') return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold tier-premium">
      <Crown size={13} />
      Premium Provider
    </span>);

  if (tier === 'pro') return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold tier-pro">
      <Zap size={13} />
      Pro Provider
    </span>);

  return null;
};

export default function ProfileHero() {
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    setSaved((s) => !s);
    toast.success(saved ? 'Removed from trusted trades' : 'Added to your trusted trades list');
  };

  const handleShare = () => {
    toast.success('Profile link copied to clipboard');
  };

  const handleBookCall = () => {
    toast.success('Connecting you via voice AI — James will answer shortly');
  };

  const handleInstantQuote = () => {
    toast.success('Opening instant quote form');
  };

  const handleRequestQuote = () => {
    toast.success('Quote request sent — James will respond within 1 hour');
  };

  return (
    <div className="bg-white border-b border-gray-100">
      {/* Cover gradient */}
      <div className={`h-36 sm:h-48 bg-gradient-to-br ${PROVIDER.coverBg} relative overflow-hidden`}>
        <div className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.4) 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }} />

        {/* Breadcrumb */}
        <div className="absolute top-4 left-4 sm:left-8 flex items-center gap-1.5 text-white/60 text-sm">
          <Link href="/provider-directory" className="hover:text-white transition-colors">
            Find a Trade
          </Link>
          <ChevronRight size={14} />
          <span className="text-white/40">{PROVIDER.trade}</span>
          <ChevronRight size={14} />
          <span className="text-white/80 truncate max-w-[120px]">{PROVIDER.name}</span>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-10 sm:-mt-14 mb-6 relative z-10">
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            <AppImage
              src={PROVIDER.avatar}
              alt={PROVIDER.avatarAlt}
              width={100}
              height={100}
              className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl object-cover ring-4 ring-white shadow-lg" />

            {PROVIDER.verified &&
            <div className="absolute -bottom-1.5 -right-1.5 w-7 h-7 bg-mint-500 rounded-full flex items-center justify-center ring-2 ring-white">
                <BadgeCheck size={16} className="text-white" />
              </div>
            }
            {PROVIDER.availableNow &&
            <div className="absolute -top-1.5 -right-1.5 flex items-center gap-1 px-2 py-0.5 bg-mint-500 rounded-full text-white text-xs font-semibold ring-2 ring-white">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                Available
              </div>
            }
          </div>

          {/* Identity */}
          <div className="flex-1 min-w-0 pb-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h1 className="font-display text-2xl sm:text-3xl font-800 text-gray-900">{PROVIDER.name}</h1>
              <TierBadge tier={PROVIDER.tier} />
            </div>
            <p className="text-gray-600 font-medium mb-2">{PROVIDER.trade}</p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <MapPin size={13} />
                {PROVIDER.location} · {PROVIDER.distanceMiles} mi away
              </span>
              <span className="flex items-center gap-1">
                <Star size={13} className="text-amber-400 fill-amber-400" />
                <span className="font-semibold text-gray-800">{PROVIDER.rating}</span>
                <span>({PROVIDER.reviewCount} reviews)</span>
              </span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleSave}
              className={`p-2.5 rounded-xl border transition-all ${
              saved ?
              'bg-red-50 border-red-200 text-red-500' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`
              }
              aria-label="Save to trusted trades">

              <Heart size={18} className={saved ? 'fill-red-500' : ''} />
            </button>
            <button
              onClick={handleShare}
              className="p-2.5 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 transition-all"
              aria-label="Share profile">

              <Share2 size={18} />
            </button>
            {PROVIDER.tier === 'premium' ?
            <button
              onClick={handleBookCall}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold text-sm hover:from-amber-600 hover:to-amber-700 transition-all active:scale-95 shadow-sm">

                <Phone size={15} />
                Book a Call
              </button> :
            PROVIDER.tier === 'pro' ?
            <button
              onClick={handleInstantQuote}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-plum-800 text-white font-semibold text-sm hover:bg-plum-900 transition-all active:scale-95 shadow-sm">

                <Zap size={15} />
                Instant Quote
              </button> :

            <button
              onClick={handleRequestQuote}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-plum-800 text-white font-semibold text-sm hover:bg-plum-900 transition-all active:scale-95 shadow-sm">

                <MessageSquare size={15} />
                Request Quote
              </button>
            }
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 pb-6 border-t border-gray-50 pt-4">
          {[
          { id: 'stat-rating', icon: Star, label: 'Rating', value: String(PROVIDER.rating), iconClass: 'text-amber-400' },
          { id: 'stat-reviews', icon: MessageSquare, label: 'Reviews', value: String(PROVIDER.reviewCount), iconClass: 'text-blue-500' },
          { id: 'stat-jobs', icon: Briefcase, label: 'Jobs done', value: String(PROVIDER.jobsDone), iconClass: 'text-plum-600' },
          { id: 'stat-response', icon: Clock, label: 'Response', value: PROVIDER.responseTime, iconClass: 'text-mint-600' },
          { id: 'stat-member', icon: Calendar, label: 'Member since', value: PROVIDER.memberSince, iconClass: 'text-gray-400' },
          { id: 'stat-completion', icon: Award, label: 'Completion', value: `${PROVIDER.completionRate}%`, iconClass: 'text-mint-500' }].
          map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.id} className="flex flex-col items-center text-center p-3 bg-gray-50 rounded-xl">
                <Icon size={16} className={`${stat.iconClass} mb-1`} />
                <p className="font-display font-700 text-gray-900 text-base tabular-nums">{stat.value}</p>
                <p className="text-gray-400 text-xs">{stat.label}</p>
              </div>);

          })}
        </div>
      </div>
    </div>);

}